import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react';

const PageWrapper = styled.div`
  background: #0e1217;
  min-height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const BlogPostContainer = styled.main`
  background: #0e1217;
  color: white;
  min-height: 100vh;
  width: 100%;
  padding-top: 64px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #28c0ef;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0;
  margin-bottom: 2rem;
  transition: color 0.2s;

  &:hover {
    color: white;
  }
`;

const BlogContent = styled.article`
  max-width: 800px;
  margin: 0 auto;
  padding: 4rem 2rem;

  h1 {
    font-size: 3.5rem;
    color: #28c0ef;
    margin-bottom: 1rem;
  }

  h2 {
    font-size: 2rem;
    color: #28c0ef;
    margin: 3rem 0 1.5rem;
    padding-top: 2rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  h3 {
    font-size: 1.5rem;
    color: rgba(255, 255, 255, 0.9);
    margin: 2rem 0 1rem;
  }

  .meta {
    color: rgba(255, 255, 255, 0.6);
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }

  .caption {
    font-size: 1.4rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 3rem;
    line-height: 1.6;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
  }

  ul {
    color: rgba(255, 255, 255, 0.8);
    margin: 1.5rem 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.75rem;
      line-height: 1.6;
    }
  }
`;

const BlogNavigation = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 4rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const NavButton = styled.button`
  background: none;
  border: none;
  color: #28c0ef;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 1rem;
  transition: all 0.2s;
  border-radius: 8px;

  &:hover {
    background: rgba(40, 192, 239, 0.1);
  }

  .label {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9rem;
    display: block;
  }

  .title {
    color: #28c0ef;
    font-size: 1.1rem;
  }
`;

const ProgressBar = styled.div`
  position: fixed;
  top: 64px;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.1);
  z-index: 1000;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: var(--progress, 0%);
    background: #28c0ef;
    transition: width 0.1s;
  }
`;

const Summary = styled.div`
  background: rgba(40, 192, 239, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0 3rem;
  border: 1px solid rgba(40, 192, 239, 0.2);

  h4 {
    color: #28c0ef;
    margin-bottom: 1rem;
    font-size: 1.1rem;
  }

  p {
    color: rgba(255, 255, 255, 0.9);
    font-size: 1rem;
    margin-bottom: 1rem;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul {
    color: rgba(255, 255, 255, 0.9);
    margin: 1rem 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
      line-height: 1.6;
    }
  }
`;

// First, let's create a type for our blog post structure
type BlogPost = {
  title: string;
  caption: string;
  date: string;
  readTime: string;
  content: string;
  summary: {
    overview: string;
    keyPoints: string[];
    readingTime: string;
  };
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  // This would normally come from an API or CMS
  const getBlogContent = (slug: string) => {
    switch(slug) {
      case 'introducing-elm':
        return {
          title: 'Introducing ELM',
          caption: 'A new era in educational technology',
          date: 'February 15, 2025',
          readTime: '7 min read',
          content: `
            ## Introduction
            Today marks a significant milestone in educational technology as we introduce ELM 
            (Educational Language Model), our groundbreaking AI system designed specifically 
            for education.

            ## What Makes ELM Different
            Unlike general-purpose AI models, ELM has been trained exclusively on educational 
            content and pedagogical methodologies. This specialized training enables:

            • Deep understanding of curriculum standards
            • Age-appropriate content generation
            • Multi-language support for global education
            • Real-time adaptation to student needs
            • Privacy-first design for educational data

            ## Technical Innovation
            ELM's architecture represents a leap forward in educational AI:

            • Advanced natural language processing for education
            • Real-time content adaptation capabilities
            • Multi-modal learning support
            • Built-in assessment tools
            • Secure data handling protocols

            ## Real-World Impact
            Our beta testing with 500 teachers across 50 schools shows remarkable results:

            • 40% reduction in lesson planning time
            • 60% improvement in student engagement
            • 35% increase in assignment completion
            • 45% better standardized test performance
            • 90% teacher satisfaction rate

            ## Looking Forward
            The launch of ELM is just the beginning. Our roadmap includes:

            • Enhanced personalization features
            • Expanded subject coverage
            • New collaboration tools
            • Advanced analytics
            • Global education initiatives
          `,
          summary: {
            overview: "Introducing ELM, our revolutionary AI system built specifically for education, transforming how teachers create and deliver educational content.",
            keyPoints: [
              "Specialized AI model for education",
              "Trained on extensive educational data",
              "Proven results from beta testing",
              "Comprehensive roadmap for future development"
            ],
            readingTime: "This 7-minute read explores how ELM is revolutionizing educational technology."
          }
        };
      case 'spark-l1-education-llm':
        return {
          title: 'Spark L1: World\'s First Education LLM',
          caption: 'Breaking new ground in AI for education',
          date: 'February 13, 2025',
          readTime: '5 min read',
          content: `
            ## Introduction
            We're proud to introduce Spark L1, a revolutionary tool designed specifically for educators. 
            This breakthrough technology empowers teachers with capabilities that transform how they 
            prepare, teach, and support their students.

            ## Key Features
            Spark L1 understands the unique challenges educators face daily. It helps teachers create 
            engaging lesson plans, develop personalized worksheets, and generate creative classroom 
            activities - all aligned with curriculum standards and best teaching practices.

            Here's what teachers can accomplish with Spark L1:
            • Create differentiated learning materials in seconds
            • Generate culturally relevant examples and explanations
            • Develop interactive assessments and quizzes
            • Produce clear, age-appropriate content for any subject
            • Design engaging homework assignments and projects

            ## Real-World Impact
            Early feedback from our pilot program has been exceptional. Teachers report saving hours 
            of preparation time while creating more engaging and effective educational materials.

            ### Teacher Testimonials
            One middle school teacher shared: "Spark L1 helps me create materials that would have 
            taken hours to develop. Now I can spend more time working directly with my students."

            ## Global Reach
            The platform also supports multiple languages and can adapt content for different grade 
            levels, making it an invaluable tool for educators worldwide. Whether you're teaching 
            mathematics, literature, science, or any other subject, Spark L1 helps you create 
            materials that resonate with your students.
          `,
          summary: {
            overview: "Introducing Spark L1, the first Language Model trained exclusively on educational content and classroom data, designed to understand and support real teaching needs.",
            keyPoints: [
              "Trained on millions of validated educational materials",
              "Understands curriculum standards and teaching methodologies",
              "Built with real classroom data and teacher input",
              "Supports multiple languages and education systems"
            ],
            readingTime: "This 5-minute read introduces how Spark L1 is revolutionizing educational content creation with its education-first approach."
          }
        };
      case 'future-of-ai-in-education':
        return {
          title: 'The Future of AI in Education',
          caption: 'Transforming learning experiences',
          date: 'January 15, 2025',
          readTime: '6 min read',
          content: `
            Artificial Intelligence is reshaping education in profound ways. From personalized 
            learning paths to automated assessment, AI is helping teachers focus more on what 
            matters most: meaningful interactions with students.

            We envision a future where AI acts as a powerful assistant to teachers, handling 
            routine tasks and providing insights that help create more engaging, effective 
            learning experiences. This isn't about replacing teachers – it's about empowering them.

            The possibilities are endless: AI can adapt content to different learning styles, 
            provide real-time feedback, and help identify areas where students need additional support.
          `
        };
      case 'ai-assessment-revolution':
        return {
          title: 'AI-Powered Assessment',
          caption: 'Transforming how we evaluate learning',
          date: 'February 15, 2025',
          readTime: '8 min read',
          content: `
            The traditional methods of student assessment are being revolutionized by artificial intelligence. 
            At SparkSkool, we're leading this transformation with innovative AI-powered assessment tools that 
            provide deeper insights into student learning.

            Our AI assessment system goes beyond traditional grading. It analyzes student work in real-time, 
            identifying patterns, strengths, and areas for improvement. This allows teachers to provide more 
            targeted and timely feedback, helping students learn and grow more effectively.

            The system also adapts to each student's pace and learning style, ensuring assessments are both 
            challenging and achievable. This personalized approach helps maintain student engagement while 
            providing more accurate measurements of their progress.
          `
        };
      case 'personalized-learning-paths':
        return {
          title: 'The Power of Personalized Learning',
          caption: "Every student's unique journey",
          date: 'February 12, 2025',
          readTime: '6 min read',
          content: `
            Education is not one-size-fits-all, and at SparkSkool, we're using AI to create truly personalized 
            learning experiences. Our platform analyzes each student's learning patterns, preferences, and pace 
            to create customized learning paths.

            Through sophisticated algorithms and machine learning, we can identify when a student is struggling 
            with a concept and automatically adjust the difficulty level or provide additional support. Similarly, 
            when a student shows mastery, we can introduce more challenging material to keep them engaged.

            This dynamic approach ensures that every student receives the right content at the right time, 
            maximizing their learning potential and maintaining their motivation.
          `
        };
      case 'teacher-ai-collaboration':
        return {
          title: 'Teachers and AI: A Perfect Partnership',
          caption: 'Enhancing human expertise with AI',
          date: 'February 10, 2025',
          readTime: '7 min read',
          content: `
            The future of education lies in the powerful partnership between teachers and AI. At SparkSkool, 
            we're creating tools that enhance rather than replace teacher expertise, allowing educators to 
            focus on what they do best: inspiring and guiding students.

            Our AI assistants handle routine tasks like grading, lesson planning, and progress tracking, 
            giving teachers more time for meaningful interactions with students. The AI also provides 
            real-time insights about student performance, helping teachers make informed decisions about 
            their teaching strategies.

            This collaboration between human expertise and artificial intelligence creates a more efficient 
            and effective learning environment for everyone.
          `
        };
      case 'future-of-curriculum':
        return {
          title: 'Creating Better Class Materials with AI',
          caption: 'Empowering teachers with AI-powered content creation',
          date: 'February 8, 2025',
          readTime: '5 min read',
          content: `
            Creating engaging and effective class materials can be time-consuming for teachers. 
            At SparkSkool, we're transforming this process with AI tools that help teachers 
            create high-quality educational content in minutes rather than hours.

            Our AI assistant helps teachers generate various materials including:
            • Lesson plans tailored to different learning levels
            • Engaging worksheets and activities
            • Interactive quizzes and assessments
            • Visual aids and presentation slides
            • Student handouts and study guides

            The system understands educational standards and learning objectives, ensuring 
            all generated content aligns with curriculum requirements. Teachers can easily 
            customize the materials to match their teaching style and their students' needs.

            What makes our platform unique is its understanding of pedagogical best practices. 
            It suggests engaging activities, provides differentiation options, and includes 
            built-in accessibility features. Teachers remain in control, using AI as a powerful 
            tool to enhance their expertise and save valuable time.

            We're seeing teachers save 5-10 hours per week on material preparation, giving them 
            more time to focus on what matters most: interacting with their students and 
            providing personalized support.
          `
        };
      case 'global-education-access':
        return {
          title: 'Breaking Down Educational Barriers',
          caption: 'Making quality education accessible',
          date: 'February 6, 2025',
          readTime: '6 min read',
          content: `
            Access to quality education should not be limited by geography or resources. At SparkSkool, 
            we're committed to breaking down these barriers through innovative AI technology and 
            thoughtful implementation strategies.

            Our platform is designed to work across different devices and internet conditions, 
            ensuring students can access educational resources even in areas with limited connectivity. 
            We've also implemented offline capabilities, allowing students to continue learning 
            even when internet access is unavailable.

            Through partnerships with educational institutions worldwide, we're creating a network 
            of shared resources and expertise. Our AI technology helps translate and localize content, 
            making it culturally relevant and accessible to students from diverse backgrounds.

            We're also developing specialized tools for students with different learning needs, 
            ensuring that every student has the opportunity to succeed regardless of their 
            circumstances or location.
          `
        };
      case 'data-driven-insights':
        return {
          title: 'The Power of Educational Data',
          caption: 'Insights that drive improvement',
          date: 'February 4, 2025',
          readTime: '7 min read',
          content: `
            Data analytics and AI are revolutionizing how we understand and improve the learning 
            process. SparkSkool's advanced analytics platform provides unprecedented insights 
            into student learning patterns, helping educators make more informed decisions.

            Our system analyzes various data points - from engagement metrics to performance 
            trends - creating a comprehensive picture of each student's learning journey. 
            This information helps teachers identify struggling students early and provide 
            targeted support when it's most needed.

            We're also using this data to identify broader educational trends and patterns, 
            helping schools and districts optimize their curriculum and teaching strategies. 
            All of this is done while maintaining strict privacy standards and ensuring 
            data security.

            The insights gained from this data are helping create more effective, 
            evidence-based approaches to education, ultimately leading to better 
            outcomes for students.
          `
        };
      case 'emotional-intelligence-ai':
        return {
          title: 'EQ Meets AI',
          caption: 'Supporting social-emotional learning',
          date: 'February 2, 2025',
          readTime: '6 min read',
          content: `
            ## Introduction
            At SparkSkool, we're pioneering the integration of emotional intelligence into 
            educational AI, recognizing that social-emotional development is crucial for 
            student success.

            ## Key Features
            Our AI system includes sophisticated emotional intelligence capabilities:

            • Recognition of student emotional states
            • Adaptive response to stress signals
            • Personalized emotional support
            • Social skills development tools
            • Empathy-building exercises

            ## Implementation
            We've integrated EQ features throughout the platform:

            • Real-time emotion detection
            • Stress-level monitoring
            • Personalized coping strategies
            • Social interaction simulations
            • Progress tracking for emotional growth

            ## Benefits
            Early adoption has shown significant improvements:

            • Reduced student anxiety levels
            • Improved classroom participation
            • Better peer relationships
            • Enhanced self-awareness
            • Stronger emotional regulation

            ## Future Development
            We continue to enhance our EQ capabilities with:

            • Advanced emotion recognition
            • More sophisticated response systems
            • Expanded support tools
            • Cultural sensitivity features
            • Parent engagement options
          `,
          summary: {
            overview: "Discover how we're integrating emotional intelligence into our AI systems to support students' social-emotional development alongside academic growth.",
            keyPoints: [
              "Advanced emotion recognition technology",
              "Personalized emotional support tools",
              "Real-time stress monitoring",
              "Comprehensive EQ development features"
            ],
            readingTime: "This 6-minute read explores the intersection of emotional intelligence and AI in education."
          }
        };
      default:
        return null;
    }
  };

  const blogPost = getBlogContent(slug || '');

  // Add blog posts array for navigation
  const blogPosts = [
    'introducing-elm',
    'spark-l1-education-llm',
    'future-of-ai-in-education',
    'ai-assessment-revolution',
    'personalized-learning-paths',
    'teacher-ai-collaboration',
    'future-of-curriculum',
    'global-education-access',
    'data-driven-insights',
    'emotional-intelligence-ai'
  ];

  const currentIndex = blogPosts.indexOf(slug || '');
  const prevPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;

  // Add these new hooks
  const [readingProgress, setReadingProgress] = useState(0);
  const articleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (articleRef.current) {
        const element = articleRef.current;
        const totalHeight = element.clientHeight - window.innerHeight;
        const windowScrollTop = window.scrollY;
        if (windowScrollTop === 0) {
          setReadingProgress(0);
        } else if (windowScrollTop >= totalHeight) {
          setReadingProgress(100);
        } else {
          setReadingProgress((windowScrollTop / totalHeight) * 100);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Update the renderContent function to handle bullet points
  const renderContent = (content: string) => {
    let inList = false;
    let listItems: string[] = [];

    return content.split('\n').map((line, index) => {
      const trimmedLine = line.trim();
      
      // Handle h2 headers (##)
      if (trimmedLine.startsWith('## ')) {
        if (inList) {
          inList = false;
          const list = (
            <ul key={`list-${index}`}>
              {listItems.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          );
          listItems = [];
          return [list, <h2 key={index}>{trimmedLine.replace('## ', '')}</h2>];
        }
        return <h2 key={index}>{trimmedLine.replace('## ', '')}</h2>;
      }
      
      // Handle h3 headers (###)
      if (trimmedLine.startsWith('### ')) {
        if (inList) {
          inList = false;
          const list = (
            <ul key={`list-${index}`}>
              {listItems.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          );
          listItems = [];
          return [list, <h3 key={index}>{trimmedLine.replace('### ', '')}</h3>];
        }
        return <h3 key={index}>{trimmedLine.replace('### ', '')}</h3>;
      }
      
      // Handle bullet points
      if (trimmedLine.startsWith('• ')) {
        inList = true;
        listItems.push(trimmedLine.replace('• ', ''));
        if (index === content.split('\n').length - 1) {
          const list = (
            <ul key={`list-${index}`}>
              {listItems.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          );
          listItems = [];
          inList = false;
          return list;
        }
        return null;
      }
      
      // Handle regular paragraphs
      if (trimmedLine && !inList) {
        return <p key={index}>{trimmedLine}</p>;
      }
      
      return null;
    }).flat().filter(Boolean);
  };

  if (!blogPost) {
    return (
      <PageWrapper>
        <BlogPostContainer>
          <BlogContent>
            <BackButton onClick={() => navigate('/blog')}>
              <ArrowLeft size={24} weight="bold" />
              Back to Blog
            </BackButton>
            
            <h1>Coming Soon!</h1>
            <div className="caption">Stay tuned for more exciting content</div>
            
            <p>We're working on bringing you more insightful articles about AI in education. 
            In the meantime, check out our other blog posts to learn more about SparkSkool's 
            innovative educational solutions.</p>
            
            <h2>Suggested Articles</h2>
            <ul>
              <li>
                <a onClick={() => navigate('/blog/introducing-elm')} style={{ cursor: 'pointer', color: '#28c0ef' }}>
                  Introducing ELM: A New Era in Educational Technology
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/blog/spark-l1-education-llm')} style={{ cursor: 'pointer', color: '#28c0ef' }}>
                  Spark L1: World's First Education LLM
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/blog/emotional-intelligence-ai')} style={{ cursor: 'pointer', color: '#28c0ef' }}>
                  EQ Meets AI: Supporting Social-Emotional Learning
                </a>
              </li>
            </ul>
          </BlogContent>
        </BlogPostContainer>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ProgressBar style={{ '--progress': `${readingProgress}%` } as React.CSSProperties} />
      <BlogPostContainer>
        <BlogContent ref={articleRef}>
          <BackButton onClick={() => navigate('/blog')}>
            <ArrowLeft size={24} weight="bold" />
            Back to Blog
          </BackButton>
          <h1>{blogPost.title}</h1>
          <div className="meta">{blogPost.date} • {blogPost.readTime}</div>
          <div className="caption">{blogPost.caption}</div>

          {blogPost.summary ? (
            <Summary>
              <h4>Quick Summary</h4>
              <p>{blogPost.summary.overview}</p>
              <ul>
                {blogPost.summary.keyPoints.map((point, index) => (
                  <li key={index}>{point}</li>
                ))}
              </ul>
              <p>{blogPost.summary.readingTime}</p>
            </Summary>
          ) : null}

          {renderContent(blogPost.content)}
          <BlogNavigation>
            {prevPost && (
              <NavButton onClick={() => navigate(`/blog/${prevPost}`)}>
                <ArrowLeft size={20} />
                <div>
                  <span className="label">Previous</span>
                  <span className="title">{getBlogContent(prevPost)?.title}</span>
                </div>
              </NavButton>
            )}
            {nextPost && (
              <NavButton onClick={() => navigate(`/blog/${nextPost}`)}>
                <div>
                  <span className="label">Next</span>
                  <span className="title">{getBlogContent(nextPost)?.title}</span>
                </div>
                <ArrowRight size={20} />
              </NavButton>
            )}
          </BlogNavigation>
        </BlogContent>
      </BlogPostContainer>
    </PageWrapper>
  );
};

export default BlogPostPage;